/**
 * The model each AI PURPOSE uses (D631, doc 25 §1 Q-362 and §11) — governed key `ai.models`.
 *
 * Every AI the platform runs goes through ONE provider, **OpenRouter**, and the model is chosen per purpose so quality and cost can
 * be compared purpose by purpose (a small fast model for the spoken reply, a strong one for the conversation, an independent one to
 * judge quality). The value:
 *
 *   `{ "provider": "openrouter", "purposes": { "conversation": { "model": "<id>", "fallbackModel": "<id>" }, … } }`
 *
 *  - `provider` is the literal `openrouter` today; the field is there so a second provider is a new, reviewed value and not a
 *    silent reinterpretation of a model id;
 *  - a purpose is one of {@link AI_PURPOSES} (conversation, voice_reply, transcription, translation, summary, classification,
 *    qa_judge, simulation, manual_answer); a version names at least one, and a purpose it does not name is INHERITED (see below);
 *  - a model is the id OpenRouter names it (`vendor/model`, e.g. `anthropic/claude-sonnet-5.5`), with an optional fallback model used
 *    when the first is unavailable (never the same id).
 *
 * NO secret lives here: the OpenRouter API key is a provider credential in the vault (`ai.openrouter`, Admin › Integrations).
 *
 * A settings-kernel key (D393): platform and country scope, OVERRIDABLE, four eyes on every change, versioned, effective-dated,
 * content-hashed, no registry default (a purpose nobody configured simply has no model). INHERITANCE is per purpose: the country's
 * version wins for the purposes it names, the platform's version supplies the rest. Fail closed: a purpose with no model (`AI_MODEL_UNSET`),
 * or a country or platform version the reader cannot parse (`AI_MODEL_INVALID`) means NO AI call for that purpose — never a guessed model.
 * Neutral: no country or vendor is named here.
 */
import { COUNTRY_RE, effectiveBucket, isObj, onlyMembers, scopedSetting } from './central-ai-shared.js';
/** The governed key. */
export const AI_MODELS_KEY = 'ai.models';
/**
 * The vault slot (`@vello/secret-vault-store`, Admin › Integrations) that holds the OpenRouter API key. The key is write-only there
 * and is never a setting, a log line or a response; the setting above names only models.
 */
export const AI_OPENROUTER_CREDENTIAL_SLOT = 'ai.openrouter';
/** The AI purposes, in the order the Admin lists them. */
export const AI_PURPOSES = ['conversation', 'voice_reply', 'transcription', 'translation', 'summary', 'classification', 'qa_judge', 'simulation', 'manual_answer'];
/** The providers a model id can belong to. */
export const AI_PROVIDERS = ['openrouter'];
/** An OpenRouter model id: `vendor/model`, optionally with a `:variant` suffix, at most 100 characters. */
const MODEL_ID = /^[a-z0-9][a-z0-9._-]{0,40}\/[a-z0-9][a-z0-9._:-]{0,57}$/;
export const isAiPurpose = (v) => typeof v === 'string' && AI_PURPOSES.includes(v);
export const isAiModelId = (v) => typeof v === 'string' && v.length <= 100 && MODEL_ID.test(v);
export const AI_MODELS_REASON_CODES = ['AI_MODEL_RESOLVED', 'AI_MODEL_UNSET', 'AI_MODEL_INVALID'];
/** The stable issue of a value that is not an AI models value, or undefined when it is one. */
export function aiModelsIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    if (!onlyMembers(v, ['provider', 'purposes']) || !('provider' in v) || !('purposes' in v))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.provider !== 'string' || !AI_PROVIDERS.includes(v.provider))
        return 'UNKNOWN_PROVIDER';
    if (!isObj(v.purposes))
        return 'PURPOSES_NOT_AN_OBJECT';
    const entries = Object.entries(v.purposes);
    if (entries.length === 0)
        return 'NO_PURPOSE';
    for (const [purpose, entry] of entries) {
        if (!isAiPurpose(purpose))
            return 'UNKNOWN_PURPOSE';
        if (!isObj(entry) || !onlyMembers(entry, ['model', 'fallbackModel']) || !('model' in entry))
            return 'INVALID_PURPOSE_ENTRY';
        if (!isAiModelId(entry.model))
            return 'INVALID_MODEL_ID';
        if ('fallbackModel' in entry) {
            if (!isAiModelId(entry.fallbackModel))
                return 'INVALID_FALLBACK_MODEL_ID';
            if (entry.fallbackModel === entry.model)
                return 'FALLBACK_SAME_AS_MODEL';
        }
    }
    return undefined;
}
export function validAiModelsValue(v) {
    return aiModelsIssue(v) === undefined;
}
/** The value normalised: purposes in the catalogue's order, each entry with only its members. */
function normalise(v) {
    const purposes = {};
    for (const p of AI_PURPOSES) {
        const e = v.purposes[p];
        if (e !== undefined)
            purposes[p] = { model: e.model, ...(e.fallbackModel !== undefined ? { fallbackModel: e.fallbackModel } : {}) };
    }
    return { provider: v.provider, purposes };
}
/** Build a valid version (platform or country scope). Throws a RangeError on a malformed scope ref or value. */
export function aiModelsSetting(input) {
    const issue = aiModelsIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid AI models: ${issue}`);
    return scopedSetting({ key: AI_MODELS_KEY, scope: input.scope, ...(input.scopeRef !== undefined ? { scopeRef: input.scopeRef } : {}), value: normalise(input.value), version: input.version, effectiveFrom: input.effectiveFrom, ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}), mode: 'OVERRIDABLE' });
}
/**
 * The model for `purpose` in `country` at `at`, over the GOVERNED reader's versions: the country's version when it names the
 * purpose, else the platform's, else nothing (`AI_MODEL_UNSET`). A version either bucket holds that cannot be read fails the
 * purpose closed (`AI_MODEL_INVALID`) even when the other bucket could answer: a half-readable configuration is not trusted.
 */
export function resolveAiModel(settings, country, purpose, at) {
    if (!COUNTRY_RE.test(country) || !isAiPurpose(purpose))
        return { ok: false, reasonCode: 'AI_MODEL_INVALID', purpose };
    const buckets = [];
    for (const b of [effectiveBucket(settings, AI_MODELS_KEY, 'country', country, at), effectiveBucket(settings, AI_MODELS_KEY, 'platform', undefined, at)]) {
        if (b === undefined)
            continue;
        if (!validAiModelsValue(b.value))
            return { ok: false, reasonCode: 'AI_MODEL_INVALID', purpose };
        buckets.push(b);
    }
    for (const b of buckets) {
        const value = b.value;
        const entry = value.purposes[purpose];
        if (entry === undefined)
            continue;
        return {
            ok: true,
            reasonCode: 'AI_MODEL_RESOLVED',
            purpose,
            provider: value.provider,
            model: entry.model,
            ...(entry.fallbackModel !== undefined ? { fallbackModel: entry.fallbackModel } : {}),
            source: b.scope,
            policyVersion: b.policyVersion,
            ...(b.contentHash !== undefined ? { contentHash: b.contentHash } : {}),
            ...(b.changeId !== undefined ? { changeId: b.changeId } : {}),
        };
    }
    return { ok: false, reasonCode: 'AI_MODEL_UNSET', purpose };
}
//# sourceMappingURL=ai-models-policy.js.map